import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import './Modal.css';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  description,
  children, 
  size = 'md',
  showCloseButton = true,
  className = '',
  gradientHeader = false,
  icon: Icon,
  animation = 'slide'
}) => {
  // ── Handle body scroll ──────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '8px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0';
    };
  }, [isOpen]);

  // ── Handle Escape key ──────────────────────────────────────────────
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[90vw]',
  };

  const animations = {
    slide: 'modal-slide',
    scale: 'modal-scale',
    fade: 'modal-fade',
    bounce: 'modal-bounce'
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`modal-overlay ${animations[animation]}`} 
      onClick={handleBackdropClick}
    >
      <div className={`modal-container ${sizes[size]} ${className}`}>
        {/* Header with optional gradient */}
        <div className={`modal-header ${gradientHeader ? 'modal-header-gradient' : ''}`}>
          <div className="modal-header-content">
            <div className="modal-title-wrapper">
              {Icon && (
                <div className="modal-icon-wrapper">
                  <Icon className="modal-icon" />
                </div>
              )}
              <div>
                {title && <h2 className="modal-title">{title}</h2>}
                {description && <p className="modal-description">{description}</p>}
              </div>
            </div>
          </div>
          {showCloseButton && (
            <button onClick={onClose} className="modal-close-btn" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>

        {/* Decorative element */}
        <div className="modal-glow"></div>
      </div>
    </div>
  );
};

export default Modal;